import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { IoIosAdd } from 'react-icons/io'
import { BiTrash } from 'react-icons/bi'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import Modal from './Modal'
import styles from './styles.module.scss'
import { deletePeriodNutrition, getGroupNutritionList, getPeriodNutritionList } from '../../actions/directoryCanteen'

export default function PeriodNutrition() {
  const dispatch = useDispatch()
  const { periodNutritionList, groupNutritionList, loading } = useSelector(state => state.directoryCanteen)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortField, setSortField] = useState('name')
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getPeriodNutritionList())
    dispatch(getGroupNutritionList())
  }, [])

  const openModal = useCallback((data) => () => {
    setSelectedItem(data || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.xp_key, name: item.name })
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deletePeriodNutrition(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }, [sortField])

  const sortedList = useMemo(() => {
    if (!periodNutritionList) return []
    return [...periodNutritionList].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortAsc ? aVal - bVal : bVal - aVal
      return sortAsc
        ? String(aVal).localeCompare(String(bVal), 'ru')
        : String(bVal).localeCompare(String(aVal), 'ru')
    })
  }, [periodNutritionList, sortField, sortAsc])

  const isModalOpen = selectedItem !== null

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <IoIosAdd size={64} className={styles.emptyIcon} />
      <p>Нет добавленных периодов питания</p>
      <Button onClick={openModal()}>Добавить первый период питания</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка периодов питания...</span>
    </div>
  )

  const renderList = () => (
    <>
      <div className={styles.tableContainer} style={{ '--table-width': '900px' }}>
        <div className={styles.table}>
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Название</th>
                <th>Группа питания</th>
                <th onClick={() => handleSort('start')} style={{ cursor: 'pointer' }}>Начало</th>
                <th onClick={() => handleSort('stop')} style={{ cursor: 'pointer' }}>Окончание</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedList.map((item) => (
                <tr
                  key={item.xp_key}
                  onClick={openModal(item)}
                  className={styles.tableRow}
                >
                  <td>
                    <span className={styles.itemName}>{item.name}</span>
                  </td>
                  <td>
                    <span className={styles.itemDescription}>
                      {groupNutritionList?.find(el => el.xp_key == item.xp_eat_group)?.name || '—'}
                    </span>
                  </td>
                  <td>{item.start || '—'}</td>
                  <td>{item.stop || '—'}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={handleDelete(item)}
                      title={`Удалить период питания ${item.name}`}
                      aria-label={`Удалить период питания ${item.name}`}
                    >
                      <BiTrash size={20} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.actions}>
          <span className={styles.counter}>
            Всего: {sortedList.length}
          </span>
          <Button onClick={openModal()}>
            <IoIosAdd size={20} style={{ marginRight: 8 }} />
            Добавить период питания
          </Button>
        </div>
      </div>
    </>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Периоды питания</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderList()}
      </main>

      <Modal
        closeModal={closeModal}
        data={selectedItem}
        width={'830px'}
        addMode={!selectedItem?.xp_key}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить ${confirmation.name}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}
